import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/mockups/ui";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Search, Rocket, IdCard, CalendarRange, Wallet, CalendarDays, HelpCircle,
  Info, ChevronRight, FolderKanban, Receipt, Target, Clock, ArrowLeftRight,
  Building2, Contact, Store, Layers, Timer, UsersRound, CalendarClock,
  ShieldCheck, Plug, DatabaseBackup, Coins,
} from "lucide-react";
import { cn } from "@/lib/utils";

const RH_ADMIN_ROLES = ["Administrador", "Administración"];
const BUDGET_ROLES = ["Administrador", "Directora"];

const GROUP_ORDER = [
  "Primeros pasos", "Operaciones", "CRM", "Dining",
  "Portal del empleado", "RRHH", "Administración", "Ayuda",
];

/* Pantallazos reales por sección (servidos estáticamente desde /public). */
const SECTION_IMG = {
  "primeros-pasos": "/manual-img/primeros-pasos.png",
  "proyectos": "/manual-img/proyectos.png",
  "presupuestos": "/manual-img/presupuestos.png",
  "cierre-proyectos": "/manual-img/cierre-proyectos.png",
  "horas": "/manual-img/horas.png",
  "pagos-externos": "/manual-img/pagos-externos.png",
  "empresas": "/manual-img/empresas.png",
  "contactos": "/manual-img/contactos.png",
  "dining-general": "/manual-img/dining-general.png",
  "dining-facturas": "/manual-img/dining-facturas.png",
  "dining-presupuesto": "/manual-img/dining-presupuesto.png",
  "dining-waves": "/manual-img/dining-waves.png",
  "mi-ficha": "/manual-img/mi-ficha.png",
  "mi-agenda": "/manual-img/mi-agenda.png",
  "nomina": "/manual-img/nomina.png",
  "festivos": "/manual-img/festivos.png",
  "registro-horario": "/manual-img/registro-horario.png",
  "equipo": "/manual-img/equipo.png",
  "solicitudes-vacaciones": "/manual-img/solicitudes-vacaciones.png",
  "permisos": "/manual-img/permisos.png",
  "integraciones": "/manual-img/integraciones.png",
  "copias-seguridad": "/manual-img/copias-seguridad.png",
  "faq": null,
};

/* --- Pequeños componentes de presentación --- */
const H = ({ children }) => <h3 className="text-base font-semibold mt-7 first:mt-0 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>;
const Steps = ({ items }) => (
  <ol className="space-y-2 mb-3">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-sm">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[11px] font-semibold text-primary">{i + 1}</span>
        <span className="leading-relaxed">{t}</span>
      </li>
    ))}
  </ol>
);
const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground mb-3">
    {items.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
  </ul>
);
const Tip = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-gold/30 bg-gold/[0.06] p-3 text-sm mb-3">
    <Info className="h-4 w-4 shrink-0 text-[#a9741c] dark:text-gold mt-0.5" />
    <span className="leading-relaxed">{children}</span>
  </div>
);
const Who = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm mb-3">
    <ShieldCheck className="h-4 w-4 shrink-0 text-primary mt-0.5" />
    <span className="leading-relaxed"><strong>Quién lo usa:</strong> {children}</span>
  </div>
);

/*
 * Cada sección lleva un "gate" que replica EXACTAMENTE el gateo del menú lateral:
 *   { always: true }        -> todo el mundo
 *   { module: "projects" }  -> can(module, "view")
 *   { roles: [...] }        -> el rol del usuario está en la lista
 *   { rhGated: true }       -> registro horario activo o rol admin de RRHH
 */
const SECTIONS = [
  {
    id: "primeros-pasos",
    group: "Primeros pasos",
    title: "Primeros pasos",
    icon: Rocket,
    gate: { always: true },
    keywords: "login iniciar sesión google perfil foto tema oscuro claro notificaciones campana cerrar sesión menú manual buscar temporizador jornada",
    body: (
      <>
        <H>Iniciar sesión</H>
        <P>La plataforma usa el acceso de Google de la empresa; no hay contraseña propia.</P>
        <Steps items={[
          "Abre la web de la plataforma. Verás la pantalla de bienvenida de Grabarte.",
          "Pulsa “Continuar con Google”.",
          "Elige tu cuenta @grabarte360.com y acepta. Entrarás directamente al Dashboard.",
          "Si es tu primera vez, tu usuario se crea solo; un Administrador te asignará el rol correcto si hiciera falta.",
        ]} />
        <H>El menú lateral</H>
        <P>A la izquierda está el <strong>menú</strong>, agrupado por bloques (Operaciones, CRM, Dining, Portal del empleado, RRHH, Administración, Ayuda). <strong>Solo ves los bloques y secciones para los que tienes permiso</strong>. Con el botón de la esquina superior del menú puedes plegarlo para ganar espacio.</P>
        <H>La barra superior</H>
        <Bullets items={[
          "A la derecha, tu nombre y tu rol (y tu foto de Google).",
          "El icono de sol/luna cambia entre modo claro y oscuro.",
          "La campana muestra tus avisos (por ejemplo, cuando aprueban tus vacaciones). El número rojo indica avisos sin leer.",
          "Si el control de jornada está activo, verás también el atajo para fichar (iniciar/parar tu jornada).",
        ]} />
        <H>Buscar en este manual</H>
        <P>Usa la caja <strong>“Buscar en el manual…”</strong> de arriba para filtrar las guías por palabra clave (por ejemplo, “vacaciones”, “factura”, “wave”).</P>
        <H>Cerrar sesión</H>
        <P>Abre tu perfil (arriba a la derecha) y pulsa cerrar sesión. Por seguridad, hazlo si usas un ordenador compartido.</P>
        <Tip>¿No ves una sección que esperabas? Es por tu rol. Pídele acceso a un Administrador (lo gestiona en “Permisos”).</Tip>
      </>
    ),
  },

  /* ---------------- OPERACIONES ---------------- */
  {
    id: "proyectos",
    group: "Operaciones",
    title: "Proyectos",
    icon: FolderKanban,
    gate: { module: "projects" },
    keywords: "proyectos crear detalle presupuesto equipo finanzas ingresos gastos facturas cronograma de pago horas estados margen miembros",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Proyectos</strong> vive cada trabajo de la agencia con toda su información: presupuesto, equipo, ingresos, gastos y horas. Es el centro de la operación.</P>
        <H>Ver y buscar proyectos</H>
        <Steps items={[
          "Entra en “Proyectos” en el menú. Verás el listado con su referencia, cliente y estado.",
          "Usa el buscador para encontrar uno por código o nombre.",
          "Según tu rol, verás todos los proyectos (Administrador, Directora, Administración) o solo aquellos en los que participas (Cuentas y Diseñador).",
        ]} />
        <H>Crear un proyecto</H>
        <Steps items={[
          "Pulsa “Nuevo proyecto” (arriba a la derecha).",
          "Rellena los datos básicos: nombre/referencia, cliente y responsable.",
          "Si el proyecto va a intermediar pagos a terceros, marca “Añadir Pagos externos” (viene desmarcado; no todos los proyectos lo necesitan). Podrás activarlo o quitarlo más adelante desde la ficha del proyecto.",
          "Guarda. El proyecto se abre en su ficha, listo para añadir presupuesto, equipo y finanzas.",
        ]} />
        <Tip>La pestaña de <strong>Pagos externos</strong> solo aparece en los proyectos donde la has activado. Dentro de la ficha, con el botón “Añadir Pagos externos” / “Quitar Pagos externos” (Administrador o responsable del proyecto) la activas o la quitas cuando quieras. Quitarla no borra ningún pago: solo deja de mostrarse en el proyecto.</Tip>
        <H>Las pestañas de un proyecto</H>
        <P>Al abrir un proyecto verás varias pestañas. Esto es lo que hace cada una:</P>
        <Bullets items={[
          "Resumen: datos generales, estado y cifras principales del proyecto.",
          "Presupuesto: las partidas y sus importes, con el margen. Según tu acceso podrás gestionarlo, editarlo o solo verlo.",
          "Equipo: las personas que participan y qué pueden ver/editar del presupuesto.",
          "Finanzas: los ingresos (facturas) y los gastos del proyecto.",
          "Pagos externos (solo si está activada): los pagos a proveedores vinculados a este proyecto.",
          "Cronograma de pago: el calendario de facturación previsto.",
          "Horas: el tiempo imputado al proyecto.",
        ]} />
        <H>Proceso: gestionar el Equipo del proyecto</H>
        <Steps items={[
          "Abre la pestaña “Equipo”.",
          "Añade a una persona con el selector y el botón de añadir.",
          "Marca qué acceso tiene al presupuesto: ver o editar. (Editar solo puede concederse a personas con rol Cuentas.)",
          "No se puede quitar al creador del proyecto. Añadir/quitar miembros lo hacen el creador o Admin/Directora/Administración.",
        ]} />
        <H>Proceso: Finanzas del proyecto</H>
        <P>En <strong>Ingresos</strong> ves las facturas (previstas, emitidas y cobradas). En <strong>Gastos</strong> ves los gastos que metes a mano <strong>más</strong> los que llegan solos de Dining y de Pagos externos.</P>
        <Steps items={[
          "Para un apunte manual, usa “Añadir gasto” o “Añadir ingreso”, con concepto, importe y fecha.",
          "Los apuntes de Dining y de Pagos externos salen con un candado y la etiqueta correspondiente: no se editan a mano porque se sincronizan solos.",
        ]} />
        <Tip>Lo que puedes ver o editar del presupuesto depende de tu rol y de si eres miembro del proyecto. Si necesitas más acceso, pídelo a Dirección.</Tip>
        <Who>Administrador y Directora gestionan todo; Administración ve todos los proyectos; Cuentas y Diseñador ven solo los suyos.</Who>
      </>
    ),
  },
  {
    id: "cronograma-pago",
    group: "Operaciones",
    title: "Cronograma de pago",
    icon: CalendarClock,
    gate: { module: "projects" },
    keywords: "cronograma de pago facturación previsión partida porcentaje fecha iva 21 pendiente de emisión regenerar plan de facturación",
    body: (
      <>
        <H>Qué es</H>
        <P>El <strong>Cronograma de pago</strong> (dentro de cada proyecto) sirve para planificar <strong>cuándo y cuánto se factura</strong>, como una hoja de Excel. Cada línea genera una <strong>previsión de factura</strong> en los Ingresos del proyecto.</P>
        <H>Crear el cronograma paso a paso</H>
        <Steps items={[
          "Abre el proyecto y entra en la pestaña “Cronograma de pago”.",
          "Añade una línea.",
          "Elige si facturas una partida concreta del presupuesto o el total.",
          "Indica el porcentaje a facturar (por ejemplo, 30%) y la fecha prevista.",
          "El sistema calcula solo la base y el IVA (21%). Se va guardando automáticamente.",
          "Añade tantas líneas como hitos de cobro tenga el proyecto (por ejemplo, 30% al inicio, 40% a mitad, 30% al final).",
        ]} />
        <H>Qué pasa al validar</H>
        <P>Al validar el cronograma, cada línea crea una <strong>previsión de factura</strong> en Ingresos con estado <strong>“Pendiente de emisión”</strong> e IVA al 21%.</P>
        <Bullets items={[
          "Si recalculas, solo se regeneran las previsiones que siguen en “Pendiente de emisión” y vienen del cronograma.",
          "Las facturas que ya avanzaron de estado (emitida, cobrada) o que editaste a mano se respetan y no se tocan.",
          "Si borras una línea del cronograma, su previsión pendiente desaparece.",
        ]} />
        <Tip>Revisa que la suma de porcentajes cuadre con lo que vas a facturar del proyecto.</Tip>
        <Who>El acceso es el mismo que el del Presupuesto del proyecto.</Who>
      </>
    ),
  },
  {
    id: "presupuestos",
    group: "Operaciones",
    title: "Presupuestos",
    icon: Receipt,
    gate: { module: "projects" },
    keywords: "presupuestos oportunidades teamleader deals clientes importes buscar comercial crm",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Presupuestos</strong> consultas las oportunidades y presupuestos que llegan de <strong>Teamleader</strong> (el CRM comercial). Son el punto de partida para abrir proyectos.</P>
        <H>Cómo trabajar aquí</H>
        <Steps items={[
          "Entra en “Presupuestos”.",
          "Busca por cliente o concepto.",
          "Abre un presupuesto para ver sus importes y detalles.",
        ]} />
        <Tip>Si no ves datos o faltan los últimos, hay que sincronizar Teamleader. Lo hace un Administrador desde “Integraciones”.</Tip>
      </>
    ),
  },
  {
    id: "cierre-proyectos",
    group: "Operaciones",
    title: "Cierre de proyectos",
    icon: Target,
    gate: { roles: BUDGET_ROLES },
    keywords: "cierre proyectos informe márgenes rentabilidad previsto real resultado dirección",
    body: (
      <>
        <H>Qué es</H>
        <P>El <strong>Cierre de proyectos</strong> es un informe de dirección: compara lo <strong>previsto</strong> con lo <strong>real</strong> y muestra el <strong>margen</strong> de cada proyecto, para saber cuáles han sido rentables.</P>
        <H>Cómo leerlo</H>
        <Bullets items={[
          "Para cada proyecto ves ingresos, gastos y el margen resultante.",
          "Un margen en negativo indica que los costes superan a los ingresos cargados hasta la fecha (revisa si faltan ingresos por imputar).",
          "Úsalo para detectar desviaciones y aprender de cara a futuros presupuestos.",
        ]} />
        <Who>Administrador y Directora.</Who>
      </>
    ),
  },
  {
    id: "horas",
    group: "Operaciones",
    title: "Horas",
    icon: Clock,
    gate: { module: "timesheets" },
    keywords: "horas imputar tiempo proyectos registro dedicación partes coste",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Horas</strong> registras el tiempo que dedicas a cada proyecto. Sirve para calcular el coste real del proyecto. <strong>No</strong> es el fichaje de jornada (eso es “Registro horario”).</P>
        <H>Imputar tus horas</H>
        <Steps items={[
          "Entra en “Horas”.",
          "Elige el proyecto al que has dedicado tiempo.",
          "Indica la fecha y el número de horas; añade una breve descripción si quieres.",
          "Guarda. Esas horas suman al coste del proyecto y a tus horas de la semana.",
        ]} />
        <Tip>Imputa las horas con regularidad (a diario o al final del día) para que los costes de proyecto sean fiables.</Tip>
      </>
    ),
  },
  {
    id: "pagos-externos",
    group: "Operaciones",
    title: "Pagos externos",
    icon: ArrowLeftRight,
    gate: { module: "external_payments" },
    keywords: "pagos externos proveedor factura 3,5% previsión fecha prevista de pago estimación facturado pagada cobrado comisión",
    body: (
      <>
        <H>Qué es</H>
        <P>Los <strong>Pagos externos</strong> son pagos a proveedores que se vinculan a un proyecto. Al crearlos, se reflejan solos en las finanzas del proyecto, sin meterlos a mano.</P>
        <H>Crear un pago externo</H>
        <Steps items={[
          "Entra en “Pagos externos” y pulsa crear.",
          "Indica el proveedor, el concepto y el importe.",
          "Vincúlalo al proyecto correspondiente.",
          "Rellena la “Fecha prevista de pago”: se usará como fecha de la previsión en Gastos.",
          "Guarda.",
        ]} />
        <H>Dónde aparece y el 3,5%</H>
        <Bullets items={[
          "En Finanzas → Gastos del proyecto: el importe que se paga al proveedor, SIN el 3,5%, como previsión de pago.",
          "En Finanzas → Facturas/Ingresos del proyecto: el importe al cliente CON el +3,5%.",
          "Dentro del proyecto, en su pestaña “Pagos externos”, con todo el detalle.",
        ]} />
        <H>Estados del pago (proceso completo)</H>
        <Steps items={[
          "Estimación: al crearlo. Genera la previsión de gasto y el ingreso previsto.",
          "Facturado: el ingreso pasa a pendiente de cobro.",
          "Pagada factura vinculada: el gasto al proveedor pasa a PAGADO.",
          "Cobrado: el ingreso del cliente queda cobrado.",
        ]} />
        <Tip>Los apuntes creados desde aquí salen con un candado en Finanzas: no se editan a mano para que no se descuadren; cámbialos avanzando el estado del pago externo.</Tip>
        <H>Importar pagos desde otro sistema (.xlsx o .zip)</H>
        <Steps items={[
          "Pulsa “Importar” (arriba a la derecha) y elige la pestaña “Desde archivo”.",
          "Selecciona el Excel (.xlsx) o el .zip exportado del otro sistema. Sube el .zip si quieres que se guarden también los PDF de las facturas.",
          "Elige el modo: “Reemplazar todo” sustituye todos los pagos externos por los del archivo; “Actualizar” solo cambia los que ya existen.",
          "Pulsa “Previsualizar” para ver cuántos pagos trae y a qué proyectos se vinculan, y luego “Importar”.",
        ]} />
        <Tip>Cada pago se vincula a su proyecto por el número de referencia (por ejemplo 0545). Al importarlos, se generan solos los gastos e ingresos en las finanzas de cada proyecto. Si subes el <strong>.zip</strong>, las facturas en PDF se adjuntan a cada pago y se abren desde el icono de factura de la lista de Pagos externos. También puedes traerlos desde un enlace en la pestaña “Desde enlace”.</Tip>
        <Who>Quien tenga acceso al módulo de Pagos externos.</Who>
      </>
    ),
  },

  /* ---------------- CRM ---------------- */
  {
    id: "empresas",
    group: "CRM",
    title: "Empresas",
    icon: Building2,
    gate: { module: "crm" },
    keywords: "empresas clientes proveedores fichas crm datos contacto directorio",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Empresas</strong> tienes el directorio de clientes y proveedores con sus datos.</P>
        <H>Cómo usarlo</H>
        <Steps items={[
          "Busca una empresa por nombre en el listado.",
          "Abre su ficha para ver sus datos, sus contactos y su actividad.",
          "Crea o edita una empresa cuando aparezca un cliente o proveedor nuevo.",
        ]} />
      </>
    ),
  },
  {
    id: "contactos",
    group: "CRM",
    title: "Contactos",
    icon: Contact,
    gate: { module: "crm" },
    keywords: "contactos personas clientes proveedores teléfono email fichas crm vincular empresa",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Contactos</strong> están las personas vinculadas a las empresas (su gente de contacto), con teléfonos y correos a mano.</P>
        <H>Cómo usarlo</H>
        <Steps items={[
          "Busca un contacto por nombre.",
          "Crea uno nuevo y vincúlalo a su empresa.",
          "Mantén sus datos al día para facilitar la comunicación.",
        ]} />
      </>
    ),
  },

  /* ---------------- DINING ---------------- */
  {
    id: "dining-general",
    group: "Dining",
    title: "Dining · Restaurantes, eventos y asistencia",
    icon: Store,
    gate: { module: "dining" },
    keywords: "dining restauración catering resumen restaurantes eventos asistencia pax sesiones mínimo precio iva wave activa",
    body: (
      <>
        <H>Qué es Dining y qué es una Wave</H>
        <P><strong>Dining</strong> es el módulo de catering por restaurantes. Dentro trabajas siempre sobre una <strong>Wave</strong> (una campaña/temporada). Arriba puedes cambiar la wave activa; todo lo que ves depende de ella.</P>
        <H>Resumen</H>
        <P>La pantalla <strong>Resumen</strong> da la foto general de la wave activa: restaurantes, eventos y estado de la facturación.</P>
        <H>Restaurantes</H>
        <Steps items={[
          "Entra en “Restaurantes”.",
          "Crea o edita un restaurante con su precio por comensal (pax), su IVA y su mínimo garantizado.",
          "Estos datos son la base del cálculo de facturas.",
        ]} />
        <H>Eventos</H>
        <Steps items={[
          "Entra en “Eventos”.",
          "Crea las sesiones/comidas por restaurante, con su fecha y los pax previstos.",
        ]} />
        <H>Asistencia</H>
        <P>En <strong>Asistencia</strong> registras los comensales <strong>reales</strong> frente al mínimo pactado. Esa diferencia es la que luego genera excedentes en la factura.</P>
        <Who>Administrador y Directora.</Who>
      </>
    ),
  },
  {
    id: "dining-facturas",
    group: "Dining",
    title: "Dining · Facturas y Cashout",
    icon: Receipt,
    gate: { module: "dining" },
    keywords: "dining facturas anticipo 10% restante 90% excedente cashout pagos restaurantes finanzas gastos proyecto ciudad",
    body: (
      <>
        <H>Cómo se generan las facturas</H>
        <P>Las facturas de cada restaurante se desglosan por fases:</P>
        <Bullets items={[
          "Anticipo 10%: el adelanto inicial.",
          "Restante 90%: el grueso del importe.",
          "Excedentes: lo que supera el mínimo según la asistencia real.",
        ]} />
        <P>Cada factura lleva su fecha e IVA del restaurante.</P>
        <H>Cashout</H>
        <P>El <strong>Cashout</strong> es la previsión de pagos a los restaurantes, agrupada por ciudad y restaurante. Te dice cuánto hay que pagar y cuándo.</P>
        <H>Cómo llega a los proyectos</H>
        <P>Cuando una wave está <strong>vinculada a un proyecto</strong>, sus facturas aparecen automáticamente como <strong>gastos</strong> en Finanzas → Gastos de ese proyecto (por ejemplo, “Anticipo 10% Urrechu”), en modo solo lectura (con candado).</P>
      </>
    ),
  },
  {
    id: "dining-presupuesto",
    group: "Dining",
    title: "Dining · Presupuesto y empleados",
    icon: Coins,
    gate: { module: "dining" },
    keywords: "dining presupuesto partidas empleados extras periodos pagados coste personal catering",
    body: (
      <>
        <H>Presupuesto</H>
        <P>En el <strong>Presupuesto</strong> de Dining controlas las partidas e importes de la wave.</P>
        <H>Empleados</H>
        <Steps items={[
          "Entra en “Empleados”.",
          "Gestiona al personal del catering: sus extras, periodos y notas por mes.",
          "Estos datos se tienen en cuenta en los costes de la wave.",
        ]} />
      </>
    ),
  },
  {
    id: "dining-waves",
    group: "Dining",
    title: "Dining · Waves, vinculación e importación",
    icon: Layers,
    gate: { module: "dining" },
    keywords: "dining waves vincular proyecto 1 a 1 enlazar desvincular enviar a proyectos importar excel actualizar datos",
    body: (
      <>
        <H>Qué es una Wave</H>
        <P>Una <strong>Wave</strong> agrupa todo el catering de una campaña (restaurantes, eventos, facturas…).</P>
        <H>Vincular una wave a un proyecto</H>
        <Steps items={[
          "Entra en “Waves”.",
          "Mira la columna “Proyecto vinculado”.",
          "Si la wave está libre, elige un proyecto de la lista para enlazarla.",
          "Si ya está vinculada, verás la referencia del proyecto y el botón para desvincular.",
        ]} />
        <Tip>El enlace es <strong>uno a uno</strong>: una wave solo puede ir con un proyecto, y un proyecto con una sola wave. Si una wave ya está ocupada, primero hay que desvincularla.</Tip>
        <H>Enviar a Proyectos e Importar</H>
        <Bullets items={[
          "Enviar a Proyectos: vuelca los datos de catering al proyecto vinculado.",
          "Importar: actualiza/carga la información de Dining cuando llega una exportación nueva. Puedes subir el archivo .zip del export directamente o pegar su enlace, y elegir “Actualizar” o “Reemplazar todo”.",
        ]} />
      </>
    ),
  },

  /* ---------------- PORTAL DEL EMPLEADO ---------------- */
  {
    id: "mi-ficha",
    group: "Portal del empleado",
    title: "Mi ficha",
    icon: IdCard,
    gate: { always: true },
    keywords: "mi ficha datos personales perfil empleado dirección teléfono puesto",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Mi ficha</strong> ves tus datos como empleado: tu información personal y laboral que tiene la empresa.</P>
        <H>Qué hacer aquí</H>
        <Steps items={[
          "Entra en “Mi ficha” (dentro del Portal del empleado).",
          "Revisa que tus datos de contacto y laborales sean correctos.",
          "Si algo está mal o desactualizado, avisa a Administración para que lo corrija.",
        ]} />
        <Tip>Revisa tu ficha de vez en cuando, sobre todo si cambias de teléfono, dirección o cuenta bancaria.</Tip>
      </>
    ),
  },
  {
    id: "mi-agenda",
    group: "Portal del empleado",
    title: "Mi agenda y vacaciones",
    icon: CalendarRange,
    gate: { always: true },
    keywords: "agenda calendario vacaciones ausencias pedir solicitar días festivos equipo google calendar conectar sincronizar justificante baja médica",
    body: (
      <>
        <H>Tu calendario</H>
        <P>En <strong>Mi agenda</strong> ves, en un sitio, tus <strong>vacaciones</strong> y <strong>ausencias</strong>, los <strong>festivos</strong> y las <strong>tareas con fecha</strong>. Tiene tres pestañas: <strong>Mi calendario</strong>, <strong>Equipo</strong> (quién está fuera) y <strong>Vacaciones</strong> (tus solicitudes).</P>
        <P>Los colores: azul = vacaciones, naranja = ausencia, borde punteado ámbar = solicitado (pendiente), oro = festivo, morado = tarea.</P>
        <H>Pedir vacaciones o una ausencia</H>
        <Steps items={[
          "En “Mi calendario”, haz clic en el primer día y luego en el último para seleccionar el rango.",
          "Abajo aparece una barra con los días elegidos: pulsa “Solicitar estos días”.",
          "Elige el tipo (vacaciones o ausencia) y añade un motivo.",
          "Si es una baja médica, adjunta el justificante (es obligatorio en ese caso).",
          "Envía. Queda en pendiente (borde punteado) hasta que la aprueben; recibirás un aviso con la decisión.",
        ]} />
        <H>Conectar tu Google Calendar</H>
        <Steps items={[
          "En Mi agenda, arriba, pulsa “Conectar Google Calendar”.",
          "Inicia sesión con tu cuenta @grabarte360.com y acepta los permisos.",
          "A partir de ahí, cada vacación aprobada se crea sola como evento de todo el día en tu calendario. Si se rechaza o anula, el evento desaparece.",
          "Puedes desconectarlo cuando quieras desde el mismo botón (“Desconectar”).",
        ]} />
        <Tip>La conexión es personal: cada quien conecta su propio calendario y solo ve sus propios eventos.</Tip>
      </>
    ),
  },
  {
    id: "nomina",
    group: "Portal del empleado",
    title: "Nómina",
    icon: Wallet,
    gate: { always: true },
    keywords: "nómina nominas salario recibo pdf descargar mes",
    body: (
      <>
        <H>Consultar tus nóminas</H>
        <Steps items={[
          "Entra en “Nómina” (Portal del empleado).",
          "Selecciona el mes que quieras consultar.",
          "Abre o descarga el PDF de tu nómina.",
        ]} />
        <Tip>Si falta una nómina o algún dato no cuadra, avisa a Administración.</Tip>
      </>
    ),
  },
  {
    id: "festivos",
    group: "Portal del empleado",
    title: "Festivos",
    icon: CalendarDays,
    gate: { always: true },
    keywords: "festivos calendario laboral días no laborables fiestas madrid nacional",
    body: (
      <>
        <H>Calendario de festivos</H>
        <P>En <strong>Festivos</strong> consultas los días festivos de la empresa (nacionales y de la Comunidad/ciudad de Madrid). Te sirven para planificar tus vacaciones.</P>
        <P>Estos festivos también aparecen marcados en <strong>Mi agenda</strong>, junto a tus vacaciones.</P>
      </>
    ),
  },
  {
    id: "registro-horario",
    group: "Portal del empleado",
    title: "Registro horario (fichaje)",
    icon: Timer,
    gate: { rhGated: true },
    keywords: "fichar jornada entrada salida pausa comida registro horario control fichaje confirmar reabrir corrección supervisión",
    body: (
      <>
        <H>Qué es</H>
        <P>El <strong>Registro horario</strong> es el control de jornada legal: fichar tu <strong>entrada</strong>, tus <strong>pausas</strong>, la <strong>comida</strong> y tu <strong>salida</strong>. Es distinto de “Horas” (eso es tiempo por proyecto).</P>
        <H>Fichar en el día a día</H>
        <Steps items={[
          "Usa el atajo de la barra superior para iniciar jornada al llegar.",
          "Marca las pausas y la comida cuando correspondan (pausar / reanudar).",
          "Al terminar, finaliza la jornada.",
          "Confirma tu jornada del día cuando te lo pida.",
          "Si te equivocaste, pide una corrección a tu responsable desde la pantalla de Registro horario.",
        ]} />
        <H>Pantalla completa</H>
        <P>Desde el menú, “Registro horario” abre la vista con pestañas: <strong>Mi jornada</strong>, <strong>Equipo</strong>, <strong>Supervisión</strong>, <strong>Correcciones</strong> y <strong>Configuración</strong>.</P>
        <Who>Todo el equipo ficha su jornada (si el módulo está activo). Ver el equipo, supervisar, revisar correcciones y exportar: Administrador, Directora y Administración. Activarlo/configurarlo: Administrador y Administración.</Who>
      </>
    ),
  },

  /* ---------------- RRHH ---------------- */
  {
    id: "equipo",
    group: "RRHH",
    title: "Equipo",
    icon: UsersRound,
    gate: { module: "hr" },
    keywords: "equipo usuarios personas ficha rol perfil de coste coste hora invitar alta rrhh acceso proyectos horas ex-empleados reasignar",
    body: (
      <>
        <H>Qué es</H>
        <P><strong>Equipo</strong> reúne en una sola ficha la gestión de usuarios y la ficha de RRHH de cada persona. Desde el listado das de alta a alguien o abres su ficha.</P>
        <H>Dar de alta a una persona</H>
        <Steps items={[
          "Pulsa “Añadir persona”.",
          "Introduce su correo @grabarte360.com y asígnale un rol y un perfil de coste.",
          "Queda “pendiente de acceso” hasta que entre por primera vez con Google, conservando el rol que le pusiste.",
        ]} />
        <H>Las zonas de la ficha (según tu rol)</H>
        <Bullets items={[
          "Acceso y económico (rol, perfil de coste, coste/hora, proyectos, horas): Administrador y Directora.",
          "Datos de RRHH (ficha laboral, nóminas, vacaciones): Administrador y Administración.",
          "Ver la página: Administrador, Directora y Administración. (Diseñador y Cuentas usan su Portal del empleado, no esta pantalla.)",
        ]} />
        <H>Coste/hora y ex-empleados</H>
        <Bullets items={[
          "Coste/hora: si lo dejas vacío, se usa la tarifa del perfil de coste de la persona.",
          "Ex-empleados / horas sin asignar: una pestaña (solo acceso económico) para reasignar a un usuario activo las horas de personas que ya no están.",
        ]} />
        <Who>Ver: Administrador, Directora, Administración. Editar según zona (ver arriba).</Who>
      </>
    ),
  },
  {
    id: "solicitudes-vacaciones",
    group: "RRHH",
    title: "Solicitudes de vacaciones",
    icon: CalendarClock,
    gate: { module: "hr" },
    keywords: "vacaciones solicitudes aprobar rechazar ausencias justificante baja médica equipo avisos",
    body: (
      <>
        <H>Revisar y decidir solicitudes</H>
        <P>Aquí llegan las <strong>solicitudes de vacaciones y ausencias</strong> del equipo para aprobarlas o rechazarlas.</P>
        <Steps items={[
          "Abre una solicitud pendiente y revisa fechas, tipo y justificante (si lo hay).",
          "Pulsa Aprobar o Rechazar, añadiendo un mensaje si quieres explicar la decisión.",
          "La persona recibe un aviso (campana y, si procede, email) con tu decisión.",
          "Al aprobar, si la persona tiene conectado Google Calendar, la vacación se le crea sola como evento.",
        ]} />
        <Who>Ver las solicitudes: acceso al módulo de RRHH. Aprobar o rechazar: solo Administrador y Directora.</Who>
      </>
    ),
  },

  /* ---------------- ADMINISTRACIÓN ---------------- */
  {
    id: "permisos",
    group: "Administración",
    title: "Permisos",
    icon: ShieldCheck,
    gate: { module: "settings" },
    keywords: "permisos roles matriz accesos módulos ver editar administrador directora cuentas diseñador administración",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Permisos</strong> defines qué puede <strong>ver</strong> y <strong>editar</strong> cada rol en cada módulo. Es la “matriz de roles” de toda la plataforma.</P>
        <H>Cómo cambiar un permiso</H>
        <Steps items={[
          "Entra en “Permisos”.",
          "Localiza el módulo (Proyectos, CRM, RRHH, Dining, etc.) y el rol.",
          "Activa o desactiva “ver” o “editar” para ese rol.",
          "Guarda. El cambio afecta de inmediato a lo que esas personas ven en el menú (y en este manual).",
        ]} />
        <P>Roles disponibles: <strong>Administrador, Directora, Administración, Cuentas, Diseñador</strong>. El Administrador siempre tiene acceso total.</P>
        <Who>Acceso al módulo de Ajustes/Configuración (normalmente Administrador).</Who>
      </>
    ),
  },
  {
    id: "integraciones",
    group: "Administración",
    title: "Integraciones",
    icon: Plug,
    gate: { module: "settings" },
    keywords: "integraciones teamleader google calendar conectar sincronizar oauth ajustes deals importar horas excel costes externos",
    body: (
      <>
        <H>Qué es</H>
        <P>En <strong>Integraciones</strong> (Ajustes) conectas la plataforma con servicios externos.</P>
        <H>Teamleader</H>
        <Steps items={[
          "Pulsa conectar Teamleader y autoriza con la cuenta de la empresa.",
          "Lanza una sincronización para traer presupuestos y oportunidades.",
          "Puedes desconectarlo cuando quieras.",
        ]} />
        <H>Costes externos (Excel)</H>
        <Steps items={[
          "En “Integraciones”, baja hasta “Costes externos (Excel)”.",
          "Elige el Excel exportado de Teamleader (con las columnas Costes y Nº de proyecto).",
          "Pulsa “Importar costes”: cada coste se reparte a su proyecto como gasto previsto y sustituye la importación anterior.",
        ]} />
        <H>Subir las horas del equipo por proyecto (Administrador y Directora)</H>
        <Steps items={[
          "Entra en “Integraciones” y baja hasta “Horas del equipo por proyecto (Excel)”.",
          "Elige el Excel de horas (columnas Fecha, Duración (en minutos), Usuario, Fase, Proyecto, Descripción).",
          "Pulsa “Analizar Excel”: verás por persona el periodo, las filas, las horas y cuántas horas previas se sustituirán.",
          "Revisa los avisos en amarillo: proyectos o personas que no existen en la app no se importan (crea el proyecto con el mismo nombre y vuelve a subir).",
          "Pulsa “Importar N registros” para guardar.",
        ]} />
        <Tip>Cada fila va al proyecto con el <strong>mismo nombre completo</strong> y a la fase (hito) con el mismo nombre. Si vuelves a subir horas del mismo periodo, <strong>se sustituyen</strong> (no se duplican). Las horas que se hayan registrado a mano en la app no se tocan.</Tip>
        <H>Google Calendar</H>
        <P>La conexión de Google Calendar es <strong>personal</strong> de cada empleado y se hace desde <strong>Mi agenda</strong> (no aquí): sirve para volcar sus vacaciones aprobadas a su propio calendario.</P>
        <Who>Acceso al módulo de Ajustes/Configuración.</Who>
      </>
    ),
  },
  {
    id: "copias-seguridad",
    group: "Administración",
    title: "Copias de seguridad",
    icon: DatabaseBackup,
    gate: { roles: ["Administrador"] },
    keywords: "copias seguridad backup google drive restaurar descargar automática diaria retención off-site conectar",
    body: (
      <>
        <H>Qué es</H>
        <P>Una <strong>copia de seguridad</strong> de todo el sistema que se guarda <strong>fuera de la app, en Google Drive</strong>. Así, pase lo que pase, los datos están a salvo y bajo tu control.</P>
        <H>Cómo funciona</H>
        <Bullets items={[
          "Cada día (de madrugada) se genera una copia completa y se sube a tu carpeta de Google Drive. La copia automática diaria solo corre en producción.",
          "Se conservan las últimas 30 copias; las más antiguas se borran solas.",
          "El estado “Conectado” confirma que Google Drive está bien configurado.",
        ]} />
        <H>Hacer, descargar o restaurar</H>
        <Steps items={[
          "“Hacer copia ahora”: crea una copia al momento (disponible también en el entorno de pruebas).",
          "Descargar: baja el archivo .zip de una copia a tu ordenador.",
          "Restaurar: devuelve el sistema al estado de esa copia. OJO: reemplaza todos los datos actuales; por eso pide escribir “RESTAURAR” para confirmar y guarda antes, automáticamente, una copia del estado actual por si necesitas volver atrás.",
        ]} />
        <Tip>Guarda tú también, de vez en cuando, una copia descargada en un sitio seguro.</Tip>
        <Who>Solo el Administrador.</Who>
      </>
    ),
  },

  /* ---------------- AYUDA ---------------- */
  {
    id: "faq",
    group: "Ayuda",
    title: "Preguntas frecuentes",
    icon: HelpCircle,
    gate: { always: true },
    keywords: "ayuda problemas no veo módulo permiso contraseña error google calendar soporte dudas",
    body: (
      <>
        <H>No veo una sección del menú</H>
        <P>Cada persona ve solo lo que corresponde a su rol. Si crees que deberías tener acceso a algo, pídeselo a un Administrador (lo gestiona en “Permisos”).</P>
        <H>¿Cómo cambio la contraseña?</H>
        <P>No hay contraseña propia de la plataforma: entras con tu cuenta de Google. La contraseña la gestionas desde tu cuenta de Google.</P>
        <H>Mis datos o mi nómina están mal</H>
        <P>Avisa a Administración indicando qué dato es incorrecto; ellos lo corrigen.</P>
        <H>No consigo conectar Google Calendar</H>
        <P>Usa tu correo @grabarte360.com al iniciar sesión de Google y acepta los permisos. Si sigue sin funcionar, inténtalo de nuevo desde “Conectar Google Calendar” en Mi agenda o avisa a un Administrador.</P>
        <H>¿Dónde fichan mis horas de jornada?</H>
        <P>El fichaje (entrada/salida) está en “Registro horario”. La dedicación a proyectos se mete en “Horas”. Son cosas distintas.</P>
      </>
    ),
  },
];

export default function ManualUso() {
  const { can, user, permissions } = useAuth();
  const { data: rhCfg } = useQuery({
    queryKey: ["rh-config-manual"],
    queryFn: async () => (await api.get("/rh/config")).data,
    retry: false, staleTime: 5 * 60 * 1000,
  });
  const rhEnabled = !!rhCfg?.enabled;
  const role = user?.role;
  const rhAdmin = RH_ADMIN_ROLES.includes(role);

  const [q, setQ] = useState("");
  const [active, setActive] = useState(SECTIONS[0].id);

  // Secciones a las que el usuario tiene acceso (mismo gateo que el menú)
  const allowed = useMemo(() => {
    return SECTIONS.filter((s) => {
      const g = s.gate;
      if (!g || g.always) return true;
      if (g.roles) return g.roles.includes(role);
      if (g.rhGated) return rhEnabled || rhAdmin;
      if (g.module) return can(g.module, "view");
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, rhEnabled, rhAdmin, permissions]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return allowed;
    return allowed.filter((s) => `${s.title} ${s.keywords}`.toLowerCase().includes(ql));
  }, [q, allowed]);

  const current = filtered.find((s) => s.id === active) || filtered[0] || null;

  const navGroups = GROUP_ORDER
    .map((name) => ({ name, items: filtered.filter((s) => s.group === name) }))
    .filter((g) => g.items.length);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="manual-page">
      <PageHeader title="Manual de uso" subtitle="Guía detallada de la plataforma. Solo ves las secciones para las que tienes permiso." />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); }}
          placeholder="Buscar en el manual…"
          className="pl-9"
          data-testid="manual-search"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Navegación de secciones, agrupada */}
        <nav className="space-y-4 lg:sticky lg:top-4 self-start">
          {navGroups.map((g) => (
            <div key={g.name} className="space-y-1">
              <div className="px-3 text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground/70">{g.name}</div>
              {g.items.map((s) => {
                const Icon = s.icon;
                const isActive = current?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(s.id)}
                    data-testid={`manual-nav-${s.id}`}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-left transition-colors",
                      isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{s.title}</span>
                    {isActive && <ChevronRight className="h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">Sin resultados para “{q}”.</p>
          )}
        </nav>

        {/* Contenido */}
        <Card className="p-6 md:p-8 min-h-[300px]" data-testid={`manual-section-${current?.id || "none"}`}>
          {current ? (
            <>
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <current.icon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold">{current.title}</h2>
              </div>
              {SECTION_IMG[current.id] && (
                <img
                  src={SECTION_IMG[current.id]}
                  alt={`Pantalla de ${current.title}`}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-full rounded-xl border border-border mb-6 shadow-sm"
                  data-testid={`manual-img-${current.id}`}
                />
              )}
              {current.body}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No hay contenido disponible.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
